// Cache för att minska API-anrop
const geocodingCache: Record<string, LocationData> = {};
import { quietLog, cityMapping } from './prayerTimesAPI';

export interface LocationData {
  latitude: number;
  longitude: number;
  city: string;
  fullLocation: string;
}

/**
 * Hämtar användarens nuvarande plats med geolocation API
 * och gör reverse geocoding för att få stadens namn
 */
export async function getCurrentLocation(): Promise<LocationData | null> {
  try {
    // Kontrollera om webbläsaren stödjer geolocation
    if (!navigator.geolocation) {
      throw new Error('Din webbläsare stödjer inte platsdetektering');
    }

    // Hämta nuvarande position med kortare timeout
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false, // Sätt till false för snabbare resultat
        timeout: 3000, // Minska timeout till 3 sekunder
        maximumAge: 60000 // Tillåt positioner som är upp till 1 minut gamla
      });
    });

    const { latitude, longitude } = position.coords;
    
    // Skapa en nyckel för cachen baserat på koordinaterna (avrundat till 2 decimaler för att minska precision)
    const cacheKey = `${latitude.toFixed(2)},${longitude.toFixed(2)}`;
    
    // Kontrollera om vi har cachad data för dessa koordinater
    if (geocodingCache[cacheKey]) {
      return geocodingCache[cacheKey];
    }
    
    // Använd reverse geocoding för att få stadens namn
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&accept-language=sv`,
        {
          headers: {
            "User-Agent": "IftarTimeApp/1.0"
          }
        }
      );
      
      const data = await response.json();
      
      // Extrahera stadens namn och land
      const city = data.address?.city || 
                  data.address?.town || 
                  data.address?.village || 
                  data.address?.municipality ||
                  'Okänd plats';
      
      const country = data.address?.country || 'Sverige';
      const fullLocation = `${city}, ${country}`;
      
      // Kontrollera om staden finns i vår lista av städer
      const knownCity = Object.keys(cityMapping).find(
        knownCity => knownCity.toLowerCase() === city.toLowerCase()
      );
      
      const result = { 
        latitude, 
        longitude, 
        city: knownCity || city, 
        fullLocation 
      };
      
      // Cachelagra resultatet
      geocodingCache[cacheKey] = result;
      
      return result;
    } catch (error) {
      quietLog('Fel vid hämtning av platsnamn', error);
      
      // Fallback-resultat
      const result = { 
        latitude, 
        longitude, 
        city: 'Okänd plats', 
        fullLocation: 'Okänd plats, Sverige' 
      };
      
      // Cachelagra även fallback-resultatet
      geocodingCache[cacheKey] = result;
      
      return result;
    }
  } catch (error) {
    quietLog('Fel vid hämtning av position', error);
    return null;
  }
}